import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { errorMessage } from "shared/api/client";
import styles from "./EditableSection.module.css";

/**
 * A settings sub-section with a per-section inline edit pattern: it shows an
 * "Edit" button that swaps the section into edit mode (Save / Cancel). There
 * is no global save — each section persists independently.
 *
 * The body is a render prop that receives the current draft and a setter:
 *   {({ editing, draft, setDraft }) => <Fields ... />}
 *
 * @param {string} title              Section heading.
 * @param {React.ReactNode} [accessory]  Optional element after the title.
 * @param {*} value                   The saved value for this section.
 * @param {(draft) => (void|Promise)} onSave  Persists the draft.
 * @param {(api) => React.ReactNode} children  Body render prop.
 */
export function EditableSection({
  title,
  accessory,
  value,
  onSave,
  onDirtyChange,
  children,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(value);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const startEdit = () => {
    setError(null);
    setDraft(value);
    setEditing(true);
  };

  const cancel = () => {
    setError(null);
    setDraft(value);
    setEditing(false);
  };

  const save = () => {
    setSaving(true);
    setError(null);
    // Only leave edit mode on success. A rejected onSave keeps the section open
    // with the draft intact and surfaces the error so the edit isn't silently
    // lost.
    Promise.resolve(onSave?.(draft))
      .then(() => {
        setEditing(false);
      })
      .catch((err) => {
        setError(
          errorMessage(err, "Couldn't save this section. Please try again.")
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>{title}</span>
          {accessory}
        </div>

        <div className={styles.actions}>
          {editing ? (
            <>
              <button
                type="button"
                className={styles.cancel}
                onClick={cancel}
                disabled={saving}
                title="Discard your changes and stop editing."
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.save}
                onClick={save}
                disabled={saving}
                title="Save your changes to this section."
              >
                Save
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.edit}
              onClick={startEdit}
              title="Edit this section."
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {children({ editing, draft, setDraft })}
        {editing && error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

EditableSection.propTypes = {
  title: PropTypes.string.isRequired,
  accessory: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  onSave: PropTypes.func.isRequired,
  // Called with true while the section has unsaved edits.
  onDirtyChange: PropTypes.func,
  children: PropTypes.func.isRequired,
};
